import { useForm } from 'react-hook-form';
import { Alert, Box, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import { Button, Input } from 'components';
import { yup } from 'utils';

type PayloadType = { code: string };
/* eslint-disable no-unused-vars */
type FormType = {
  onButton: (data: PayloadType) => void;
  counter: number;
  resendCode: () => void;
  isVerifying?: boolean;
  isResending?: boolean;
};
const Form: React.FC<FormType> = ({
  onButton,
  counter,
  resendCode,
  isVerifying = false,
  isResending = false,
}: FormType) => {
  const { t } = useTranslation('component.auth.verification.form');

  const schema = yup.object().shape({
    code: yup.string().length(6).label('code').required(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      code: '',
    },
  });

  const onSubmit = async (data: PayloadType) => {
    onButton(data);
  };

  const formProps = useInputAttributes(register, errors, {
    code: t('code'),
  });

  const cooldown = counter !== 0;

  const fieldSurface = {
    background: 'secondary',
    borderWidth: '1px',
    borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 'lg',
    _hover: { background: 'rgba(255,255,255,0.05)' },
    _focus: {
      background: 'secondary',
      borderColor: 'primary',
    },
  };

  return (
    <Box w="100%">
      {cooldown && (
        <Alert.Root
          status="info"
          mb={5}
          borderRadius="md"
          borderWidth="1px"
          borderColor="rgba(255,255,255,0.12)"
        >
          <Alert.Indicator />
          <Text color="info">{t('codeSent')}</Text>
        </Alert.Root>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" gap={4} alignItems="flex-end">
          <Box width="100%">
            <Input
              {...formProps.code}
              {...fieldSurface}
              textAlign="center"
              placeholder="     -     -     -     -     -     -     "
              disabled={isVerifying}
            />
          </Box>
          <Button
            variant="filled"
            display="block"
            minW={81}
            mb={4}
            title={cooldown ? counter.toString() : t('resend')}
            isDisabled={cooldown || isResending || isVerifying}
            isLoading={isResending}
            onClick={() => resendCode()}
            type="button"
          />
        </Box>
        <Button
          type="submit"
          variant="filled"
          display="block"
          title={t('submit')}
          minW="100%"
          mt={8}
          isLoading={isVerifying}
          isDisabled={isVerifying}
        />
      </form>
    </Box>
  );
};

export default Form;
